/**
 * Legacy checkout form types, validations and constants.
 * Used by the legacy checkout template (src/checkout/templates/Checkout).
 * For steps-form and app/forms/checkout use @/steps-form/checkout/checkout.types and @/constants/checkout.constants.
 */
import { FormikHelpers } from 'formik';
import { ComponentProps, ReactNode, RefObject } from 'react';
import * as Yup from 'yup';

import {
  BUY_TYPE,
  DELIVERY_OPTION,
  FINANCE_QUOTE_RADIO_TYPE,
  FINANCE_QUOTE_REPAYMENT_FREQUENCY,
  PAYMENT_METHODS_TYPE,
  PAYMENT_OPTIONS_TYPE,
  TRADE_IN_TYPE,
  USE_FINANCE_TYPE,
} from '@/constants/checkout.constants';
import { CheckoutSocialProof } from 'src/checkout/components/CheckoutSocialProof/CheckoutSocialProof';
import { regexAbn, regexMobilePhone } from 'src/constants/regex.constants';
import { DiscountCodes, DiscountsResponse } from 'src/data/CheckoutApi/CheckoutApi.types';
import {
  FINANCE_EMPLOYMENT_STATUS,
  FINANCE_FAMILY_STATUS,
  FINANCE_RESIDENCY_STATUS,
  FINANCE_RESIDENTIAL_STATUS,
  FinanceQuote,
  RESOLUTION_OF_STATUS_VISA_SUBCLASS,
} from 'src/data/FinanceApi/FinanceApi.types';
import { FeesType } from 'src/data/PaymentApi/PaymentApi.types';
import { MICROCOPY, MicrocopyKey } from 'src/data/microcopy/microcopyDictionary';
import { DICOUNT_BENEFIT_MICROCOPY_KEYS } from 'src/general/components/DiscountCard/Discount.types';
import {
  OptionalInputAddressAutocompleteValidation,
  getInputAddressAutocompleteValidation,
} from 'src/general/components/forms/FormikInputAddressAutocomplete/FormikInputAddressAutocomplete';
import { BaseCarData } from 'src/types/Car.types';
import {
  isAfter1900,
  isOver18YearsOld,
  isUnder120YearsOld,
  isValidDate,
  isValidDateInTheFuture,
  isValidPartialDateBefore,
  isValidPartialDateInThePast,
} from 'src/utils/date';

import { CheckoutMachineContext } from '../Checkout.machine';
import { isLessThan36Months } from './CheckoutForm.helpers';
import { FinanceQuoteInfoContentForm } from './FinanceQuoteInfoContentForm';
import { FinanceQuoteContentForm } from './FinanceQuotesForm';

// Re-export enums for backward compatibility (canonical source: checkout.constants)
export {
  BUY_TYPE,
  DELIVERY_OPTION,
  FINANCE_QUOTE_RADIO_TYPE,
  FINANCE_QUOTE_REPAYMENT_FREQUENCY,
  PAYMENT_METHODS_TYPE,
  PAYMENT_OPTIONS_SUMMARY_TYPE,
  PAYMENT_OPTIONS_TYPE,
  SEND_YOUX_STATUS,
  TERMS_AND_CONDITIONS,
  TRADE_IN_TYPE,
  USE_FINANCE_TYPE,
} from '@/constants/checkout.constants';

export const buyTypes = [
  { value: BUY_TYPE.PERSONAL, label: MICROCOPY.CHECKOUT_STEP_BUY_TYPE_OPTION_PERSONAL },
  { value: BUY_TYPE.BUSINESS, label: MICROCOPY.CHECKOUT_STEP_BUY_TYPE_OPTION_BUSINESS },
] as const;

const paymentOptions = [
  { value: PAYMENT_OPTIONS_TYPE.INSTANT_FINANCE_QUOTE, label: MICROCOPY.CHECKOUT_PAYMENT_OPTIONS_RADIO_ITEM_1_LABEL },
  { value: PAYMENT_OPTIONS_TYPE.CARMA_CONTACT, label: MICROCOPY.CHECKOUT_PAYMENT_OPTIONS_RADIO_ITEM_2_LABEL },
  { value: PAYMENT_OPTIONS_TYPE.MY_OWN_FUND, label: MICROCOPY.CHECKOUT_PAYMENT_OPTIONS_RADIO_ITEM_3_LABEL },
] as const;

export const tradeIn = [
  { value: TRADE_IN_TYPE.TRADE_IN, label: MICROCOPY.CHECKOUT_TRADE_IN_RADIO_ITEM_1_LABEL },
  { value: TRADE_IN_TYPE.NO_TRADE_IN, label: MICROCOPY.CHECKOUT_TRADE_IN_RADIO_ITEM_2_LABEL },
] as const;

const getStartedTradeIn = [
  { value: TRADE_IN_TYPE.TRADE_IN, label: MICROCOPY.CHECKOUT_TRADE_IN_RADIO_ITEM_1_LABEL },
  { value: TRADE_IN_TYPE.NO_TRADE_IN, label: MICROCOPY.CHECKOUT_TRADE_IN_RADIO_ITEM_2_LABEL },
] as const;

const getStartedFinance = [
  { value: USE_FINANCE_TYPE.FINANCE, label: MICROCOPY.CHECKOUT_TRADE_IN_RADIO_ITEM_1_LABEL },
  { value: USE_FINANCE_TYPE.NO_FINANCE, label: MICROCOPY.CHECKOUT_TRADE_IN_RADIO_ITEM_2_LABEL },
] as const;

export const deliveryOptions = [
  {
    label: MICROCOPY.CHECKOUT_DELIVERY_OPTION_FORM_DELIVERY_LABEL,
    value: DELIVERY_OPTION.DELIVERY,
  },
  {
    value: DELIVERY_OPTION.COLLECTION,
    label: MICROCOPY.CHECKOUT_DELIVERY_OPTION_FORM_COLLECTION_LABEL,
  },
] as const;

export const byofCarmaFinanceOptionTypes = [
  { label: 'Carma finance', value: 'carmaFinance' },
  { label: 'Angle Finance', value: 'angleFinance' },
  { label: 'ANZ', value: 'ANZ' },
  { label: 'CBA', value: 'CBA' },
  { label: 'Finance One', value: 'financeOne' },
  { label: 'Latitude Financial Services', value: 'latitudeFinancialServices' },
  { label: 'Liberty Finance', value: 'libertyFinance' },
  { label: 'Macquarie', value: 'macquarie' },
  { label: 'Money3', value: 'money3' },
  { label: 'NAB', value: 'NAB' },
  { label: 'Pepper Money', value: 'pepperMoney' },
  { label: 'Plenti', value: 'plenti' },
  { label: 'St.George', value: 'st.George' },
  { label: 'Stratton Finance', value: 'strattonFinance' },
  { label: 'Westpac', value: 'westpac' },
  { label: 'Wisr', value: 'wisr' },
  { label: 'Other', value: 'other' },
] as const;

const paymentMethodsTypes = [
  {
    value: PAYMENT_METHODS_TYPE.RESERVATION,
    label: MICROCOPY.CHECKOUT_PAYMENT_SUMMARY_FORM_PAYMENT_METHOD_RESERVATION,
  },
  {
    value: PAYMENT_METHODS_TYPE.FULL_AMOUNT,
    label: MICROCOPY.CHECKOUT_PAYMENT_SUMMARY_FORM_PAYMENT_METHOD_PAY_FULL_AMOUNT,
  },
] as const;

export const familyStatusOptions = [
  {
    value: FINANCE_FAMILY_STATUS.SINGLE,
    label: 'Single',
  },
  {
    value: FINANCE_FAMILY_STATUS.MARRIED,
    label: 'Married',
  },
  {
    value: FINANCE_FAMILY_STATUS.DE_FACTO,
    label: 'De Facto',
  },
  {
    value: FINANCE_FAMILY_STATUS.WIDOWED,
    label: 'Widowed',
  },
  {
    value: FINANCE_FAMILY_STATUS.SEPARATED,
    label: 'Separated',
  },
  {
    value: FINANCE_FAMILY_STATUS.DIVORCED,
    label: 'Divorced',
  },
  {
    value: FINANCE_FAMILY_STATUS.OTHER,
    label: 'Other',
  },
];

export const residentialStatusOptions = [
  {
    value: FINANCE_RESIDENTIAL_STATUS.HOMEOWNER,
    label: "I'm a homeowner",
  },
  {
    value: FINANCE_RESIDENTIAL_STATUS.RENTING,
    label: "I'm renting",
  },
  {
    value: FINANCE_RESIDENTIAL_STATUS.BOARDER,
    label: "I'm a boarder",
  },
  {
    value: FINANCE_RESIDENTIAL_STATUS.LIVING_WITH_PARENTS,
    label: "I'm living with parents",
  },
  {
    value: FINANCE_RESIDENTIAL_STATUS.EMPLOYER_SUPPLIED_ACCOMMODATION,
    label: 'Employer supplied accommodation',
  },
];

export const employmentStatusOptions = [
  {
    value: FINANCE_EMPLOYMENT_STATUS.FULL_TIME,
    label: 'Full time',
  },
  {
    value: FINANCE_EMPLOYMENT_STATUS.PART_TIME,
    label: 'Part time',
  },
  {
    value: FINANCE_EMPLOYMENT_STATUS.CASUAL,
    label: 'Casual',
  },
  {
    value: FINANCE_EMPLOYMENT_STATUS.CONTRACT,
    label: 'Contract',
  },
  {
    value: FINANCE_EMPLOYMENT_STATUS.SELF_EMPLOYED,
    label: 'Self-employed',
  },
  {
    value: FINANCE_EMPLOYMENT_STATUS.RETIRED,
    label: 'Retired',
  },
  {
    value: FINANCE_EMPLOYMENT_STATUS.UNEMPLOYED,
    label: 'Unemployed',
  },
];

export const residencyStatusOptions = [
  {
    value: FINANCE_RESIDENCY_STATUS.AUSTRALIAN_CITIZEN,
    label: 'Australian citizen',
  },
  {
    value: FINANCE_RESIDENCY_STATUS.PERMANENT_RESIDENT,
    label: 'Permanent resident',
  },
  {
    value: FINANCE_RESIDENCY_STATUS.TEMPORARY_RESIDENT,
    label: 'Temporary resident',
  },
];

export const financeQuoteInfoRadioTypes = [
  {
    value: FINANCE_QUOTE_RADIO_TYPE.YES_OPTION,
    label: 'Yes',
  },
  {
    value: FINANCE_QUOTE_RADIO_TYPE.NO_OPTION,
    label: 'No',
  },
];

export const repaymentFrequencyOptions = [
  {
    label: 'Weekly',
    value: FINANCE_QUOTE_REPAYMENT_FREQUENCY.WEEKLY,
  },
  {
    label: 'Monthly',
    value: FINANCE_QUOTE_REPAYMENT_FREQUENCY.MONTHLY,
  },
];

export type CheckoutPayloadPaymentOptionFinanceQuoteRequest = {
  quoteRequestId: string;
};
type CheckoutPayloadPaymentOptionFinanceQuote = {
  quoteId: string | null;
  quoteInfo?: FinanceQuote;
  adjusted_monthly_repayment_amount_cents?: number;
  repaymentFrequency?: FINANCE_QUOTE_REPAYMENT_FREQUENCY;
};

/**
 * Full checkout payload
 * Used when finishing the checkout
 */
export type CheckoutPayload = Prettify<
  (
    | { buyType: { buyType: BUY_TYPE.PERSONAL } }
    | { buyType: { buyType: BUY_TYPE.BUSINESS }; businessInfo: BusinessInfoFormValues }
  ) & {
    buyerInfo: BuyerInfoFormValues;
  } & {
    deliveryOption: Omit<DeliveryOptionsFormValues, 'addressDeliverable'>;
    carmaExtendedCoverageOption: Omit<CarmaExtendedCoverageFormValues, 'manufacturerWarrantyInMonths'>;
    hasTradeIn: TradeInFormValues;
    customLineItems: any[];
    lineItems: any[];
    totalPrice: any;
  } & (
      | {
          paymentOption: { paymentOption: PAYMENT_OPTIONS_TYPE.MY_OWN_FUND };
        }
      | {
          paymentOption: { paymentOption: PAYMENT_OPTIONS_TYPE.CARMA_CONTACT };
          financeContact: BYOFCarmaFinanceFormValues;
        }
      | {
          paymentOption: { paymentOption: PAYMENT_OPTIONS_TYPE.INSTANT_FINANCE_QUOTE };
          financeQuoteInfo: FinanceQuoteInfoFormValues;
          financeQuoteRequest: CheckoutPayloadPaymentOptionFinanceQuoteRequest;
          financeQuote: CheckoutPayloadPaymentOptionFinanceQuote;
        }
    ) & {
      billingAddress: PaymentSummaryFormValues['billingAddress'];
      paymentMethod: PAYMENT_METHODS_TYPE;
    }
>;

/**
 * Helper type
 * All properties in checkout payload without unions
 */
export type CheckoutPayloadAll = Prettify<
  Omit<UnionToIntersection<CheckoutPayload>, 'buyType' | 'paymentOption'> & { buyType: BuyTypeFormValues } & {
    paymentOption: PaymentOptionsFormValues;
    discountCodes: DiscountCodes[];
    discounts: DiscountsResponse;
    nrmaToken?: string | null;
  }
>;

/**
 * Partial checkout payload
 * Used when completing the checkout
 */
export type CheckoutPayloadPartial = Prettify<Partial<CheckoutPayloadAll>>;

export type CheckoutFormValues =
  | BuyTypeFormValues
  | BusinessInfoFormValues
  | BuyerInfoFormValues
  | DeliveryOptionsFormValues
  | CarmaExtendedCoverageFormValues
  | TradeInFormValues
  | PaymentOptionsFormValues
  | BYOFCarmaFinanceFormValues
  | FinanceQuotesFormValues
  | FinanceQuoteInfoFormValues
  | PaymentSummaryFormValues;

export interface CheckoutFormProps<T extends CheckoutFormValues> {
  values: Partial<T>;
  /**
   * Handle form submission
   * @returns true if successful, string with error message otherwise
   */
  onSubmit: (values: T, formikHelpers: FormikHelpers<T>) => Promise<true | string>;
  onSubmitTracking?: (success: boolean, reasons?: string[]) => void;
  onBack?: () => void;
  setValuesPartial?: (
    values: CheckoutPayloadPartial,
    changePaymentMethod?: boolean,
    updateFinanceInformation?: boolean,
  ) => Promise<void>;
  submitText: MicrocopyKey;
  hideTitle?: boolean;
  isUseFinance?: PAYMENT_OPTIONS_TYPE;
  isSummaryStep?: boolean;
}

export interface CheckoutFormCheckoutProps<T extends CheckoutFormValues, M extends MicrocopyTypes> {
  values: Partial<T>;
  /**
   * Handle form submission
   * @returns true if successful, string with error message otherwise
   */
  onSubmit: (values: T, formikHelpers: FormikHelpers<T>) => Promise<true | string>;
  onSubmitTracking?: (success: boolean, reasons?: string[]) => void;
  onBack?: () => void;
  setValuesPartial?: (
    values: CheckoutPayloadPartial,
    changePaymentMethod?: boolean,
    updateFinanceInformation?: boolean,
  ) => Promise<void>;
  submitText: ReactNode;
  hideTitle?: boolean;
  microcopy: Partial<M>;
  isUseFinance?: PAYMENT_OPTIONS_TYPE;
  isSummaryStep?: boolean;
  setOpenGovCharge?: (value: boolean) => void;
  context?: Partial<CheckoutMachineContext>;
  /** Ref to store orderId from payment response; used by PaymentSummaryForm for confirmation navigation */
  orderIdRef?: RefObject<string | null>;
}

export type MicrocopyTypes =
  | BuyTypeFormType
  | BusinessInfoFormType
  | BuyerInfoType
  | CarmaExtendedCoverageFormType
  | TradeInFormType
  | PaymentOptionsFormType
  | FinanceQuoteInfoFormType
  | FinanceQuotesFormType
  | BYOFCarmaFinanceFormType;

export type BuyTypeFormType = {
  title: ReactNode;
  description: ReactNode;
  buyType: ReactNode;
  businessLabel: ReactNode;
  personalLabel: ReactNode;
  personalOptionLabel: ReactNode;
  businessOptionLabel: ReactNode;
  checkoutSocialProof: ComponentProps<typeof CheckoutSocialProof>['microcopy'];
  joinThe1000s: ReactNode;
};

export type BusinessInfoFormType = {
  title: ReactNode;
  description: ReactNode;
  abnLabel: ReactNode;
  submitText: ReactNode;
  checkoutSocialProof: ComponentProps<typeof CheckoutSocialProof>['microcopy'];
};

export type BuyerInfoType = {
  header: ReactNode;
  description: ReactNode;
  businessAdditionInfo: ReactNode;
  formHeader: ReactNode;
  inputFirstNameLabel: ReactNode;
  inputMiddleNameLabel: ReactNode;
  inputLastNameLabel: ReactNode;
  inputDateOfBirthLabel: ReactNode;
  inputAddressHelper: ReactNode;
  inputLicenseNumberLabel: ReactNode;
  inputLicenseNumberHelper: ReactNode;
  inputLicenseNumberHelperSeeExample: ReactNode;
  submitText: ReactNode;
  checkoutSocialProof: ComponentProps<typeof CheckoutSocialProof>['microcopy'];
};

export type TradeInFormType = {
  title: ReactNode;
  description: ReactNode;
  item1: ReactNode;
  item2: ReactNode;
  radioLabel: ReactNode;
  submitText: ReactNode;
  checkoutSocialProof: ComponentProps<typeof CheckoutSocialProof>['microcopy'];
  tradeIn: { value: string; label: ReactNode }[];
};

export type CarmaExtendedCoverageFormType = {
  title: ReactNode;
  description: ReactNode;
  chooseTermLabel: ReactNode;
  chooseTermHelper: ReactNode;
  coverageOptions?: { value: string; label: ReactNode; description: ReactNode; price: number }[];
  finePrint: ReactNode;
  submitText: ReactNode;
  checkoutSocialProof: ComponentProps<typeof CheckoutSocialProof>['microcopy'];
};

export type PaymentOptionsFormType = {
  title: ReactNode;
  description: ReactNode;
  nextButtonLabel: ReactNode;
  nextFinanceButtonLabel: ReactNode;
  nextQuoteButtonLabel: ReactNode;
  nextSummaryButtonLabel: ReactNode;
  radioLabel: ReactNode;
  checkoutSocialProof: ComponentProps<typeof CheckoutSocialProof>['microcopy'];
  submitText: ReactNode;
  submitButtonModal: ReactNode;
  changePaymentOptionLabel: ReactNode;
  paymentOptions: { value: string; label: ReactNode }[];
};

export type FinanceQuoteInfoFormType = {
  title: ReactNode;
  description: ReactNode;
  submitText: ReactNode;
  contentForm: ComponentProps<typeof FinanceQuoteInfoContentForm>['microcopy'];
  checkoutSocialProof: ComponentProps<typeof CheckoutSocialProof>['microcopy'];
};

export type FinanceQuotesFormType = {
  title: ReactNode;
  submitText: ReactNode;
  financeQuoteContentForm: ComponentProps<typeof FinanceQuoteContentForm>['microcopy'];
  checkoutSocialProof: ComponentProps<typeof CheckoutSocialProof>['microcopy'];
};

export type BYOFCarmaFinanceFormType = {
  title: ReactNode;
  description: ReactNode;
  contactNameLabel: ReactNode;
  contactEmailLabel: ReactNode;
  contactMobileLabel: ReactNode;
  lenderNameOtherLabel: ReactNode;
  nameLabel: ReactNode;
  submitText: ReactNode;
  checkoutSocialProof: ComponentProps<typeof CheckoutSocialProof>['microcopy'];
};

export type PromoCardMicrocopyType = {
  discountLabels: {
    [key in DICOUNT_BENEFIT_MICROCOPY_KEYS]: ReactNode;
  };
};

//
// Buy type
//
export const BuyTypeFormValidation = Yup.object({
  buyType: Yup.string()
    .oneOf(buyTypes.map((item) => item.value))
    .required(),
});
export type BuyTypeFormValues = ReturnType<typeof BuyTypeFormValidation.validateSync>;

export const checkIsValidAbn: Yup.TestFunction = function (value) {
  if (!value) return false;

  const cleanedValue = value.replaceAll(' ', '');
  if (cleanedValue.length !== 11) return false;

  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  const adjustedValue = Number.parseInt(cleanedValue[0], 10) - 1 + cleanedValue.substring(1);

  const total = Array.from(
    adjustedValue,
    (char: string, index: number) => Number.parseInt(char, 10) * weights[index],
  ).reduce((acc, curr) => acc + curr, 0);

  return total % 89 === 0;
};

//
// Business info
//
export const BusinessInfoFormValidation = Yup.object({
  abn: Yup.string()
    .matches(regexAbn, 'Please specify a valid ABN')
    .required(
      'Please specify your business ABN. Quickly find your ABN by visiting the [Australian Business Register (ABR)](https://abr.business.gov.au/)',
    )
    .test('check-valid-abn', 'Please specify a valid ABN', checkIsValidAbn),
  businessName: Yup.string().required('Please specify your business name'),
});
export type BusinessInfoFormValues = ReturnType<typeof BusinessInfoFormValidation.validateSync>;

//
// Buyer info
//
export const BuyerInfoFormValidation = Yup.object({
  firstName: Yup.string().required('Please add your first name'),
  middleName: Yup.string().optional(),
  lastName: Yup.string().required('Please add your last name'),
  dateOfBirth: Yup.string()
    .required("Please don't forget to include your date of birth")
    .test('birthDay', 'Invalid date of birth', (value: string) => isValidDate(value))
    .test('birthDay', 'You need to be over 18 years to purchase with us', (value: string) => isOver18YearsOld(value))
    .test('birthDay', 'That seems vintage! Please check your date of birth', (value: string) =>
      isUnder120YearsOld(value),
    ),
  address: getInputAddressAutocompleteValidation(),
  licenceNumber: Yup.string()
    .required('Please enter your licence number')
    .test(
      'len',
      'Must be a valid licence number with a minimum of 6 and maximum of 11 alphanumeric characters',
      (val) => val?.length >= 6 && val?.length <= 11 && /^[a-zA-Z0-9]+$/.test(val),
    ),
});
export type BuyerInfoFormValues = Prettify<
  ReturnType<typeof BuyerInfoFormValidation.validateSync> & Partial<BuyTypeFormValues>
>;

//
// Handover info (delivery or collection)
//
export const DeliveryOptionsValidation = Yup.object({
  deliveryMethod: Yup.string()
    .required(`Please select 'Delivery' or 'Collection' before proceeding.`)
    .oneOf(deliveryOptions.map((item) => item.value)),
  address: OptionalInputAddressAutocompleteValidation.when('deliveryMethod', {
    is: DELIVERY_OPTION.COLLECTION,
    then: OptionalInputAddressAutocompleteValidation,
    otherwise: getInputAddressAutocompleteValidation(),
  }),
  addressDeliverable: Yup.boolean().when('deliveryMethod', {
    is: DELIVERY_OPTION.DELIVERY,
    then: Yup.boolean().oneOf([true], 'Delivery address is outside of Greater Sydney region'),
  }),
  pickupAddress: getInputAddressAutocompleteValidation().optional().default(undefined),
  notes: Yup.string().optional(),
});
export type DeliveryOptionsFormValues = Omit<ReturnType<typeof DeliveryOptionsValidation.validateSync>, 'address'> & {
  address: Omit<ReturnType<typeof OptionalInputAddressAutocompleteValidation.validateSync>, 'state'> & { state: any };
};

//
// Trade-in info
//
export const TradeInFormValidation = Yup.object({
  hasTradeIn: Yup.string()
    .required("Please select Yes or No, depending on whether you have a car you'd like to trade in.")
    .oneOf(tradeIn.map((item) => item.value)),
});
export type TradeInFormValues = ReturnType<typeof TradeInFormValidation.validateSync>;

//
// Carma extended coverage
//
export const CarmaExtendedCoverageFormValidation = Yup.object({
  manufacturerWarrantyInMonths: Yup.number().required(),
  extendedCoverageOption: Yup.string().required('Please select an option to continue.'),
  price: Yup.number().required(),
});
export type CarmaExtendedCoverageFormValues = ReturnType<typeof CarmaExtendedCoverageFormValidation.validateSync>;

//
// Payment options
//
export const PaymentOptionsFormValidation = Yup.object({
  paymentOption: Yup.string()
    .required('Please select if you need any type of finance.')
    .oneOf(paymentOptions.map((item) => item.value)),
});
export type PaymentOptionsFormValues = ReturnType<typeof PaymentOptionsFormValidation.validateSync>;

const upfrontContributionMax: Yup.TestFunction = function (value) {
  return value === undefined || Number.parseInt(value, 10) <= Number.parseInt(this.parent.carPurchasePrice, 10);
};

//
// Finance quote info
//
export const FinanceQuoteInfoFormValidation = Yup.object({
  carYear: Yup.number().required(),
  carPurchasePrice: Yup.number().required(),
  isBusinessFinanceApplication: Yup.boolean().default(false),
  upfrontContribution: Yup.number()
    .typeError('Upfront contribution must be number type')
    .min(0, `Upfront contribution can't be negative`)
    .test('upfrontContributionMax', `Upfront contribution can't be above car value`, upfrontContributionMax)
    .required('Upfront contribution is required'),
  familyStatus: Yup.string().when('isBusinessFinanceApplication', {
    is: false,
    then: Yup.string()
      .required('Please select your marital status')
      .oneOf(familyStatusOptions.map((item) => item.value)),
  }),
  currentAddress: getInputAddressAutocompleteValidation(),
  residentialStatus: Yup.string()
    .oneOf(residentialStatusOptions.map((item) => item.value))
    .required('Please select your residential status'),
  currentAddressStartDate: Yup.string()
    .required('Please provide your current address move in date')
    .test('moveInDate', 'You must enter a valid date in the past', (value: string) =>
      isValidPartialDateInThePast(value),
    )
    .test('isAfter1900', 'You must enter a date after 01/1900', (value: string) => isAfter1900(value)),
  hasTenancyAgreement: Yup.string().when(['isBusinessFinanceApplication', 'residentialStatus'], {
    is: (isBusinessFinanceApplication: boolean, residentialStatus: FINANCE_RESIDENTIAL_STATUS) =>
      !isBusinessFinanceApplication &&
      [FINANCE_RESIDENTIAL_STATUS.RENTING, FINANCE_RESIDENTIAL_STATUS.EMPLOYER_SUPPLIED_ACCOMMODATION].includes(
        residentialStatus,
      ),
    then: Yup.string()
      .oneOf(financeQuoteInfoRadioTypes.map((item) => item.value))
      .required(`Please select if you're on the tenancy agreement`),
  }),
  hasMortgageOnCurrentAddress: Yup.string().when('residentialStatus', {
    is: (residentialStatus: string) => ([FINANCE_RESIDENTIAL_STATUS.HOMEOWNER] as string[]).includes(residentialStatus),
    then: Yup.string()
      .oneOf(financeQuoteInfoRadioTypes.map((item) => item.value))
      .required('Please select if you also own a property with a mortgage'),
  }),
  hasInvestmentProperty: Yup.string().when(['residentialStatus'], {
    is: (residentialStatus: FINANCE_RESIDENTIAL_STATUS) =>
      [
        FINANCE_RESIDENTIAL_STATUS.RENTING,
        FINANCE_RESIDENTIAL_STATUS.BOARDER,
        FINANCE_RESIDENTIAL_STATUS.LIVING_WITH_PARENTS,
        FINANCE_RESIDENTIAL_STATUS.EMPLOYER_SUPPLIED_ACCOMMODATION,
      ].includes(residentialStatus),
    then: Yup.string()
      .oneOf(financeQuoteInfoRadioTypes.map((item) => item.value))
      .required('Please select if you own an investment property'),
  }),
  hasMortgageOnInvestmentProperty: Yup.string().when(['residentialStatus', 'hasInvestmentProperty'], {
    is: (residentialStatus: FINANCE_RESIDENTIAL_STATUS, hasInvestmentProperty: FINANCE_QUOTE_RADIO_TYPE) =>
      [
        FINANCE_RESIDENTIAL_STATUS.RENTING,
        FINANCE_RESIDENTIAL_STATUS.BOARDER,
        FINANCE_RESIDENTIAL_STATUS.LIVING_WITH_PARENTS,
        FINANCE_RESIDENTIAL_STATUS.EMPLOYER_SUPPLIED_ACCOMMODATION,
      ].includes(residentialStatus) && hasInvestmentProperty === FINANCE_QUOTE_RADIO_TYPE.YES_OPTION,
    then: Yup.string()
      .oneOf(financeQuoteInfoRadioTypes.map((item) => item.value))
      .required('Please select if you have mortgage on an investment property'),
  }),
  investmentPropertyAddress: Yup.object().when(
    ['isBusinessFinanceApplication', 'residentialStatus', 'hasInvestmentProperty', 'hasMortgageOnInvestmentProperty'],
    {
      is: (
        isBusinessFinanceApplication: boolean,
        residentialStatus: FINANCE_RESIDENTIAL_STATUS,
        hasInvestmentProperty: FINANCE_QUOTE_RADIO_TYPE,
        hasMortgageOnInvestmentProperty: FINANCE_QUOTE_RADIO_TYPE,
      ) =>
        !isBusinessFinanceApplication &&
        [FINANCE_RESIDENTIAL_STATUS.RENTING, FINANCE_RESIDENTIAL_STATUS.EMPLOYER_SUPPLIED_ACCOMMODATION].includes(
          residentialStatus,
        ) &&
        hasInvestmentProperty === FINANCE_QUOTE_RADIO_TYPE.YES_OPTION &&
        hasMortgageOnInvestmentProperty === FINANCE_QUOTE_RADIO_TYPE.YES_OPTION,
      then: getInputAddressAutocompleteValidation(),
    },
  ),
  previousResidentialAddress: Yup.object().when('currentAddressStartDate', {
    is: (currentAddressStartDate: string) => isLessThan36Months(currentAddressStartDate),
    then: getInputAddressAutocompleteValidation(),
  }),
  previousAddressStartDate: Yup.string().when('currentAddressStartDate', (currentAddressStartDate: string) => {
    if (isLessThan36Months(currentAddressStartDate)) {
      return Yup.string()
        .required('Please provide your previous address move in date')
        .test('moveInDate', 'You must enter a valid date before your current address move in date', (value: string) =>
          isValidPartialDateBefore(value, currentAddressStartDate),
        );
    }
    return Yup.string();
  }),
  employmentStatus: Yup.string().when('isBusinessFinanceApplication', {
    is: false,
    then: Yup.string()
      .required('Please select your employment status')
      .oneOf(employmentStatusOptions.map((item) => item.value)),
  }),
  jobTitle: Yup.string().when('isBusinessFinanceApplication', {
    is: false,
    then: Yup.string().required('Please provide your job title'),
  }),
  startedEmployerDate: Yup.date()
    .typeError('Please provide your start date.')
    .when('isBusinessFinanceApplication', {
      is: false,
      then: Yup.date().typeError('Please provide your start date.').required('Please provide your start date.'),
    }),
  totalYearlyIncomeBeforeTax: Yup.number().when('isBusinessFinanceApplication', {
    is: false,
    then: Yup.number()
      .typeError('Total yearly income before tax must be number type')
      .required('Please enter your income before tax'),
  }),
  residencyStatus: Yup.string()
    .required('Please select your residency status')
    .oneOf(residencyStatusOptions.map((item) => item.value)),
  visaSubclassNumber: Yup.number().when('residencyStatus', {
    is: (residencyStatus: FINANCE_RESIDENCY_STATUS) => residencyStatus === FINANCE_RESIDENCY_STATUS.TEMPORARY_RESIDENT,
    then: Yup.number()
      .typeError('Visa subclass number must be number type')
      .required('Please enter your visa subclass number'),
  }),
  visaExpiryDate: Yup.date()
    .typeError('You must enter a valid date in the future')
    .when(['residencyStatus', 'visaSubclassNumber'], {
      is: (residencyStatus: FINANCE_RESIDENCY_STATUS, visaSubclassNumber: number) =>
        residencyStatus === FINANCE_RESIDENCY_STATUS.TEMPORARY_RESIDENT &&
        visaSubclassNumber !== undefined &&
        visaSubclassNumber.toString().trim() !== RESOLUTION_OF_STATUS_VISA_SUBCLASS,
      then: Yup.date()
        .typeError('You must enter a valid date in the future')
        .required('Please provide your visa expiry date')
        .test('visaExpiryDate', 'You must enter a valid date in the future', (value: string) =>
          isValidDateInTheFuture(value),
        ),
      otherwise: Yup.date().typeError('You must enter a valid date in the future'),
    }),
  isCheckTermsAndConditions: Yup.string().required('Please accept the terms and conditions to proceed'),
  extendedCoverageOption: Yup.string().optional(),
});
export type FinanceQuoteInfoFormValues = ReturnType<typeof FinanceQuoteInfoFormValidation.validateSync>;

//
// Select finance quote info
//
export const FinanceQuotesFormValidation = Yup.object({
  quoteRequestId: Yup.string().required(),
  quoteId: Yup.string()
    .nullable()
    .required(
      'Please select a quote to continue. If you can’t decide yet, select ‘I can’t decide yet’ and you can still secure the car.',
    ),
  buyType: Yup.string()
    .oneOf(buyTypes.map((item) => item.value))
    .required(),
  isVisaHolder: Yup.boolean().required(),
  repaymentFrequency: Yup.string()
    .oneOf(repaymentFrequencyOptions.map((item) => item.value))
    .required(),
  loanTerm: Yup.number().required(),
  carPrice: Yup.number().required(),
  deposit: Yup.number().required(),
  quoteInfo: Yup.object<FinanceQuote>().notRequired(),
  make: Yup.string().optional(),
});
export type FinanceQuotesFormValues = ReturnType<typeof FinanceQuotesFormValidation.validateSync> & {
  quoteInfo: FinanceQuote | undefined;
  adjusted_monthly_repayment_amount_cents?: number;
};

//
// Own finance info
//
export const BYOFCarmaFinanceFormValidation = Yup.object({
  lenderName: Yup.string().default('carmaFinance'),
  lenderNameOther: Yup.string()
    .optional()
    .when('lenderName', {
      is: (lenderName: string) => lenderName === 'other',
      then: Yup.string().required('Please provide your lender name'),
    }),
  contactName: Yup.string().optional(),
  contactEmail: Yup.string().optional().email('Please enter a valid email'),
  contactMobile: Yup.string().optional().matches(regexMobilePhone, 'Please enter a valid Australian mobile number'),
});
export type BYOFCarmaFinanceFormValues = ReturnType<typeof BYOFCarmaFinanceFormValidation.validateSync>;

//
// Payment Summary
//
export interface PaymentSummaryValues {
  carDetails: Pick<BaseCarData, 'model' | 'variant' | 'price' | 'imageUrl' | 'kilometers'>;
  buyTypeFormValues: BuyTypeFormValues;
  businessInfoFormValues: BusinessInfoFormValues;
  buyerInfoFormValues: BuyerInfoFormValues;
  deliveryOptionsFormValues: DeliveryOptionsFormValues;
  tradeInFormValues: TradeInFormValues;
  paymentOptionsFormValues: PaymentOptionsFormValues;
  byofCarmaFinanceFormValues: BYOFCarmaFinanceFormValues;
  financeQuotesFormValues: FinanceQuotesFormValues;
  carmaExtendedCoverageFormValues: CarmaExtendedCoverageFormValues;
  customLineItems: any[];
  lineItems: any[];
  totalPrice: any[];
  getAllFeesFormatted: () => Promise<FeesType['fees']>;
  discountData: DiscountsResponse;
}

export const PaymentSummaryFormValidation = Yup.object({
  billingAddress: getInputAddressAutocompleteValidation(),
  paymentMethod: Yup.string().when('isPayingWithoutFinance', {
    is: true,
    then: Yup.string()
      .oneOf(paymentMethodsTypes.map((item) => item.value))
      .required('Please select if you need any type of payment method'),
  }),
  notes: Yup.string().optional(),
  feesInCents: Yup.number().optional(),
  feesPercent: Yup.number().optional(),
  amountInCents: Yup.number().required(),
  isSameAddress: Yup.string().optional(),
});

export type PaymentSummaryFormValues = Required<PaymentSummaryValues> &
  ReturnType<typeof PaymentSummaryFormValidation.validateSync>;

export const GetStartedFormValidation = Yup.object({
  useFinance: Yup.string()
    .required('Please select how you’d like to pay.')
    .oneOf(getStartedFinance.map((item) => item.value)),
  tradeIn: Yup.string()
    .required('Please select if you’d like to trade in.')
    .oneOf(getStartedTradeIn.map((item) => item.value)),
});

export type GetStartedFormValues = ReturnType<typeof GetStartedFormValidation.validateSync>;
